import { createFileRoute } from "@tanstack/react-router";
import { Palette, Share2, Smartphone } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/cards-hero-v2.webp";
import designImg from "@/assets/cards-design.webp";
import sharingImg from "@/assets/cards-sharing.webp";

const IMG: Record<string, string> = { hero: heroImg, design: designImg, sharing: sharingImg };

export const Route = createFileRoute("/vi/platform/cards")({
  head: () => ({
    meta: [
      { title: "Danh thiếp kỹ thuật số — banana.cards" },
      { name: "description", content: "Danh thiếp kỹ thuật số đẹp, tùy biến hoàn toàn. Chạm, quét hoặc chia sẻ — cập nhật một lần, mọi nơi đều đổi." },
      { property: "og:title", content: "Danh thiếp kỹ thuật số — banana.cards" },
      { property: "og:description", content: "Danh thiếp kỹ thuật số đẹp, tùy biến hoàn toàn. Chạm, quét hoặc chia sẻ — cập nhật một lần, mọi nơi đều đổi." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/cards", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Danh thiếp kỹ thuật số"
      title="Một tấm thẻ đáng nhớ như"
      italic="người đứng sau nó."
      description="Thiết kế một danh thiếp kỹ thuật số đẹp mắt trong vài phút. Chọn từ layout cao cấp, thêm link, video, gallery và CTA — và cập nhật một lần để đẩy thay đổi đến mọi nơi."
      heroImage={IMG['hero']}
      metrics={[{ v: "200k+", l: "thẻ đang dùng" }, { v: "28%", l: "thêm lead" }, { v: "4.9/5", l: "đánh giá" }]}
      bullets={[
        { icon: Palette, title: "Đẹp mặc định", desc: "12+ layout, theme tùy chỉnh, video, gallery, link, giờ làm việc, dịch vụ và nhiều hơn." },
        { icon: Share2, title: "Chia sẻ mọi nơi", desc: "Chạm (NFC), QR, link, Wallet pass, chữ ký email — bạn chọn." },
        { icon: Smartphone, title: "Cập nhật trực tiếp", desc: "Đổi chức danh hoặc ảnh một lần. Mọi thẻ hiện có cập nhật tức thì." }
      ]}
      deepDives={[
        { eyebrow: "Thiết kế", title: "Sắc nét đến từng pixel, đúng thương hiệu,", italic: "luôn luôn.", description: "Chọn layout, thả logo và màu thương hiệu, sắp xếp khối như Lego. Thêm video hero, gallery ảnh, dịch vụ, FAQ và CTA trực tiếp — không cần designer.", image: IMG["design"], reverse: false, points: ["Brand kit với font và màu tùy chỉnh", "Ảnh bìa, avatar và gradient nhấn", "Khối phong phú: video, gallery, dịch vụ, FAQ", "Hiển thị từng phần & ghi đè ngôn ngữ"] },
        { eyebrow: "Chia sẻ", title: "Một thẻ, mọi", italic: "kênh.", description: "NFC, QR, link rút gọn, chữ ký email, Apple & Google Wallet — thẻ hoạt động trên mọi thiết bị, người nhận không cần cài app.", image: IMG["sharing"], reverse: true, points: ["Chạm-để-chia-sẻ qua NFC, QR hoặc link", "Pass Wallet cho iOS & Android", "Trình tạo chữ ký email", "Form thu lead trên mọi thẻ"] }
      ]}
      howItWorks={[
        { title: "Chọn layout", desc: "Bắt đầu với template do designer tạo." },
        { title: "Thêm nội dung", desc: "Ảnh, vai trò, link, dịch vụ, video." },
        { title: "Chọn cách chia sẻ", desc: "Thẻ NFC, QR, link hoặc Wallet." },
        { title: "Cập nhật một lần, mãi mãi", desc: "Mỗi thay đổi lan truyền tức thì." }
      ]}
      capabilities={[
        "Tên miền tùy chỉnh & link rút gọn",
        "Hồ sơ đa ngôn ngữ",
        "Form thu lead tích hợp sẵn",
        "Apple & Google Wallet",
        "Phần video & media phong phú",
        "Nút đặt lịch trực tiếp",
        "Phân tích trên mọi phần",
        "Template có thương hiệu cho đội"
      ]}
      faq={[
        { q: "Khác biệt giữa thẻ ảo và thẻ NFC là gì?", a: "Thẻ ảo là một hồ sơ kỹ thuật số được host mà bạn chia sẻ qua link, QR, pass Wallet, chữ ký email hoặc AirDrop — không cần vật phẩm. Thẻ NFC là thẻ Banana vật lý bạn chạm vào điện thoại của ai đó để mở cùng hồ sơ tức thì. Cả hai trỏ đến cùng một hồ sơ có thể chỉnh sửa, nên bạn đổi chức danh một lần và mọi chia sẻ cập nhật khắp nơi." },
        { q: "Người nhận có cần app để mở thẻ không?", a: "Không — thẻ của bạn mở trong bất kỳ trình duyệt mobile nào. Họ có thể lưu thông tin liên hệ vào điện thoại chỉ một lần chạm (vCard), lưu thẻ vào Apple/Google Wallet, nhắn tin, gọi, đặt lịch hoặc xem mạng xã hội — tất cả không cần tải gì." },
        { q: "Tôi có thể có nhiều thẻ không?", a: "Có — tạo không giới hạn thẻ cho các vai trò (ví dụ founder vs pitch nhà đầu tư), sự kiện (thẻ có khối landing riêng cho sự kiện), ngôn ngữ, hoặc thành viên đội. Chuyển thẻ đang dùng trên NFC bất cứ lúc nào." },
        { q: "Cả đội có thể dùng template có thương hiệu không?", a: "Có — admin xây template workspace bị khóa với logo, màu, font và footer công ty, sau đó cấp thẻ hàng loạt cho đồng nghiệp. Mỗi thành viên điền tên/chức danh/ảnh của họ nhưng thương hiệu vẫn nhất quán." },
        { q: "Nếu tôi đổi chức danh hoặc số điện thoại thì sao?", a: "Sửa một lần trong trình chỉnh sửa Banana và mọi chia sẻ — QR, NFC, pass Wallet, chữ ký email, link chia sẻ — phản ánh thay đổi tức thì. Không cần in lại, không cần tải lại." },
        { q: "Có miễn phí không?", a: "Có — cá nhân có thể dùng banana.cards miễn phí mãi mãi. Gói trả phí mở khóa template đội có thương hiệu, tên miền tùy chỉnh, phân tích nâng cao, tính năng AI, tự động hóa và tích hợp." }
      ]}
      outro={ { title: "Tạo ấn tượng đầu tiên", italic: "họ sẽ nhớ.", description: "Miễn phí cho cá nhân, cao cấp cho đội. Không cần in thẻ." } }
    />
  );
}